import { TableProps } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { partial_ratio } from 'fuzzball';
import _ from 'lodash';

import { ColumnsTypePermission, DatePickerType } from '../components';
import { ColumnItem, FilterItem } from '../components/ColumnConfig/interface';
import {
  DatePickerQuickType,
  getRangeDateFromType,
} from '../components/RangePicker/util';
import { MenuItem } from '../config/menu';
import { BaseStatus, FilterType, SubcontractorStatusEnum } from '../enum';
import { SYSTEM_COLUMN } from '../pages/engineeringManagement/specialProjects/uploadCost/interface';
import { SubcontractorStatusProgress } from '../pages/subcontractor/interface';
import { ROUTER_URL } from '../router/constants';
import { VALUE_EMPTY_TABLE } from './constants';

interface SubstitutionObject {
  [key: string]: string | number | undefined | null;
}

interface DecodedJWT {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  signature: string;
}

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Convert an object to a FormData
 * @param data the object to convert, with property values as strings
 * @returns a FormData object with the same key-value pairs
 */

export const objectToFormData = (data: { [key: string]: string }): FormData => {
  const formData = new FormData();
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key) && data[key]) {
      formData.append(key, data[key]);
    }
  }
  return formData;
};

/**
 * Remove accents and convert to ASCII
 * @param {string} str input string
 * @returns {string} the string with accents removed and converted to ASCII
 */
export const stringToASCII = (str: string) => {
  try {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd');
  } catch {
    return '';
  }
};

/**
 * Search for an ASCII-converted keyword within an ASCII-converted original string.
 *
 * @param keyword - The keyword to search for.
 * @param original - The original string to search within.
 * @returns {boolean} - Returns true if the ASCII-converted original string contains the ASCII-converted keyword; otherwise, false.
 */

export const searchASCII = (keyword: string, original: string) => {
  if (typeof original !== 'string' || typeof keyword !== 'string') return false;
  const originalToASCII = stringToASCII(original.toLowerCase());
  const keywordToASCII = stringToASCII(keyword.toLowerCase());
  return originalToASCII.includes(keywordToASCII);
};

/**
 * Converts a given date string to the 'Australia/Melbourne' timezone
 * and formats it to 'DD/MM/YYYY hh:mm A' format.
 *
 * @param {string} date - The date string to be converted and formatted.
 * @returns {string} - The formatted date string in 'Australia/Melbourne' timezone.
 */

export const formatFullDateTime = (date: string) => {
  const utcDate = dayjs.utc(date);
  const melbourneDate = utcDate.tz('Australia/Melbourne');
  return melbourneDate.format('DD/MM/YYYY hh:mm A');
};

/**
 * Converts a given date string to the 'Australia/Melbourne' timezone
 * and formats it according to the given format.
 *
 * @param {string} date - The date string to be converted and formatted.
 * @param {string} [format='DD/MM/YYYY'] - The desired format of the output date string.
 * @returns {string} - The formatted date string in 'Australia/Melbourne' timezone.
 */
export const formatDate = (date: string, format = 'DD/MM/YYYY') => {
  const utcDate = dayjs.utc(date);
  const melbourneDate = utcDate.tz('Australia/Melbourne');
  return melbourneDate.format(format);
};

export const formatUTCDate = (date: string, format = 'DD/MM/YYYY') => {
  const utcDate = dayjs.utc(date);
  return utcDate.format(format);
};

/**
 * Converts a given date string to the 'Australia/Melbourne' timezone
 * and formats it to the 'HH:mm' format.
 *
 * @param {string} date - The date string to be converted and formatted.
 * @returns {string} - The formatted date string in 'Australia/Melbourne' timezone.
 */
export const formatTime = (date: string) => {
  const utcDate = dayjs.utc(date);
  const melbourneDate = utcDate.tz('Australia/Melbourne');
  return melbourneDate.format('HH:mm');
};

/**
 * Converts a given date string to the 'Australia/Melbourne' timezone
 * and returns the resulting date as a Day.js object.
 *
 * @param {string} date - The date string to be converted.
 * @returns {Dayjs} - The given date string converted to the 'Australia/Melbourne' timezone.
 */
export const formatDateToDayJs = (date: string) => {
  const utcDate = dayjs.utc(date);
  const melbourneDate = utcDate.tz('Australia/Melbourne');
  return melbourneDate;
};

/**
 * Formats a duration into a string representation such as "14h12m25s" (or "1h2m3s"),
 * omitting zero-value units when `minimal` is true.
 * @param {number} params.time - The duration value to format.
 * @param {boolean} [params.minimal=false] - Whether to omit the hours unit when it is zero.
 * @param {boolean} [params.valueIsSecond=false] - Whether `time` is in seconds instead of minutes.
 * @returns {string} - The formatted duration string.
 */
export const formatMinutes = ({
  time,
  minimal = true,
  valueIsSecond = false,
}: {
  time: number;
  minimal?: boolean;
  valueIsSecond?: boolean;
}): string => {
  const isNegative = time < 0;
  const totalSeconds = Math.round(Math.abs(time) * (valueIsSecond ? 1 : 60));

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const sign = isNegative ? '-' : '';

  const units: [number, string][] = valueIsSecond
    ? [
        [hours, 'h'],
        [minutes, 'm'],
        [seconds, 's'],
      ]
    : [
        [hours, 'h'],
        [minutes, 'm'],
      ];

  const format = (unitList: [number, string][]) =>
    `${sign}${unitList
      .map(([value, suffix]) => `${String(value).padStart(2, '0')}${suffix}`)
      .join('')}`;

  if (minimal) {
    const [[hoursValue], ...restUnits] = units;
    return format(hoursValue !== 0 ? units : restUnits);
  }

  return format(units);
};

/**
 * Recursively searches through the given routes array to find the active route based on the given pathname.
 * Returns an array of route keys representing the active route, or an empty array if no active route is found.
 * @param {string} pathname The current URL pathname.
 * @param {MenuItem[]} routes The routes array to search.
 * @param {string[]} [parentKeys=[]] An array of parent route keys to build upon.
 * @returns {string[]} The active route keys, or an empty array if no active route is found.
 */
export const findActiveRoute = (
  pathname: string,
  routes: MenuItem[],
  parentKeys: string[] = []
): string[] | null => {
  for (const route of routes) {
    if (!route) continue;

    const { key = '' } = route;

    // Collect parent keys as we go deeper
    const currentKeys = [...parentKeys, key] as string[];

    // Check if route has 'children' (SubMenuType)
    if ('children' in route && route.children && route.children.length) {
      const found = findActiveRoute(
        pathname,
        route.children as MenuItem[],
        currentKeys
      );
      if (found && found.length) {
        return found;
      }
    }

    // Remove query params
    const cleanPath = pathname.split('?')[0];

    // Convert dynamic segments like :userId to regex
    const regexPath = String(key).replace(/:\w+/g, '\\w+');

    // Create regex to match the path flexibly
    const regex = new RegExp(`^${regexPath}(/.*)?$`);

    if (regex.test(cleanPath)) {
      return currentKeys;
    }
  }
  return [];
};

/**
 * Given a word, capitalize the first letter of the word.
 *
 * @param {string} word the word to capitalize
 * @returns {string} the capitalized word
 */

export const capitalizeFirstLetter = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * Given a URLSearchParams object, key, and value, update the
 * URLSearchParams by setting the key to the given value and return the
 * new URLSearchParams object.
 *
 * @param {{value: string, key: string, params: URLSearchParams}}
 *   The object containing the value, key and URLSearchParams object
 * @returns {URLSearchParams} The new URLSearchParams object
 */
export const updateNewParams = ({
  value,
  key,
  params,
}: {
  value: string;
  key: string;
  params: URLSearchParams;
}) => {
  const newSearchParams = new URLSearchParams(params);
  newSearchParams.set(key, value);
  return newSearchParams;
};

/**
 * Generates a new URLSearchParams object by merging new and old values.
 *
 * @param {Object.<string, string>} newValues - An object representing the new key-value pairs to be added.
 * @param {Object.<string, string>} oldValues - An object representing the existing key-value pairs to be merged with.
 * @returns {URLSearchParams} - A URLSearchParams object constructed from the merged key-value pairs.
 */
export const generateParams = ({
  newValues,
  oldValues,
}: {
  newValues: { [key: string]: string };
  oldValues: { [key: string]: string };
}) => {
  const values = { ...oldValues, ...newValues };
  return new URLSearchParams(values);
};

/**
 * Deletes a specified key from the given URLSearchParams object.
 *
 * @param {Object} paramsObj - The parameters object.
 * @param {string} paramsObj.key - The key to be deleted from the URLSearchParams.
 * @param {URLSearchParams} paramsObj.params - The URLSearchParams object from which the key should be deleted.
 * @returns {URLSearchParams} A new URLSearchParams object with the specified key removed.
 */

export const deleteParam = ({
  key,
  params,
}: {
  key: string;
  params: URLSearchParams;
}) => {
  const newSearchParams = new URLSearchParams(params);
  newSearchParams.delete(key);
  return newSearchParams;
};

/**
 * Adds an "All" option to the beginning of the given options array.
 * @param {Array<{value: string, label: string}>} options - The options array to be modified.
 * @returns {Array<{value: string, label: string}>} - The modified options array with the "All" option added.
 */
export const addAllOption = (options: { value: string; label: string }[]) => {
  return [{ value: '', label: 'All' }, ...options];
};

/**
 * Maps an array of objects with 'id' and 'displayName' properties to an array
 * of objects with 'value' and 'label' properties, suitable for use as options
 * in an Ant Design Select component.
 *
 * @param {Array<{id: string, displayName: string}>} options - The input array
 *   of objects with 'id' and 'displayName' properties.
 * @returns {Array<{value: string, label: string}>} - The output array of objects
 *   with 'value' and 'label' properties.
 */
export const formatFilterOption = (
  options: { id: string; displayName: string }[]
) => options.map(({ id, displayName }) => ({ value: id, label: displayName }));

/**
 * Maps an array of objects with 'id' properties to an array of strings.
 *
 * @param {Array<{id: string}>} dataList - The input array of objects with 'id' properties.
 * @returns {string[]} - The output array of strings.
 */
export const getIdsFromDataList = (dataList: { id: string }[]) =>
  dataList.map(({ id }) => id);

/**
 * Filters and sorts the given columns based on the given configuration and options.
 *
 * @param {ColumnsTypePermission<any>[]} columns - The columns to be filtered and sorted.
 * @param {ColumnItem[]} configColumns - The configuration of the columns, including name, order and selected status.
 * @param {{key: string, value: string}} [order] - The order configuration, including key and value.
 * @param {{permissions?: string[]}} [options] - The options, including permissions.
 * @returns {ColumnsTypePermission<any>[]} - The filtered and sorted columns.
 */
export const filterAndSortColumns = (
  columns: ColumnsTypePermission<any>[] = [],
  configColumns: ColumnItem[],
  order?: { key: string; value: string },
  options?: { permissions?: string[] }
) => {
  const newColumns: TableProps<any>['columns'] = [];
  const sortedConfigColumns = [...configColumns].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  sortedConfigColumns.forEach((configColumn) => {
    const column = columns.find(
      (col) =>
        col.key === configColumn.name &&
        (!col.permissions ||
          !options?.permissions ||
          col.permissions.every((permission) =>
            options?.permissions?.includes(permission)
          ))
    );
    if (column && configColumn.selected) {
      if (order && column.key === order.key) {
        newColumns.push({ ...column, sortOrder: order.value as any });
      } else {
        newColumns.push(column);
      }
    }
  });

  return newColumns;
};

const handleDatePickerByType = (value: [string, string, string?] | null) => {
  if (!value) return [];
  const [from, to, dType] = value;
  const dateType = dType || DatePickerQuickType.Last7Day;

  if (dateType && dateType !== DatePickerQuickType.Custom) {
    const { start, end } = getRangeDateFromType(dateType);
    // Fall back to the stored values if the quick filter type is unrecognized
    return start && end
      ? [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'), dateType]
      : [from, to, dateType];
  } else {
    return [from, to, dateType];
  }
};

/**
 * Converts an array of filter data objects into a record object.
 *
 * @param {Array<{name: string, values: any}>} filterData - The array of
 *   objects containing filter names and their corresponding values.
 * @returns {Record<string, any>} - An object where each key is a filter name
 *   and its value is the associated filter values or an empty array if no
 *   values exist.
 */

const FIELD_SUFFIX: Partial<Record<FilterType, string>> = {
  [FilterType.DatePicker]: '__btw',
  [FilterType.Select]: '__in',
};

export const convertFilterValues = (
  filterData: FilterItem[]
): Record<string, string[]> =>
  filterData.reduce<Record<string, string[]>>((obj, item) => {
    const suffix = FIELD_SUFFIX[item.uiType] ?? '';
    const values =
      item.uiType === FilterType.DatePicker
        ? handleDatePickerByType(
            item.values as [string, string, string?] | null
          )
        : item.values;

    obj[`${item.name}${suffix}`] = values ?? [];
    return obj;
  }, {});

export const baseStatusOptions = Object.entries(BaseStatus).map(
  ([key, value]) => ({
    value,
    label: key,
  })
);

export const subcontractorStatusOptions = Object.entries(
  SubcontractorStatusEnum
).map(([key, value]) => ({
  value,
  label: key,
}));

/**
 * Filters menu items based on user permissions.
 *
 * @param {MenuItem[]} items - The menu items to be filtered.
 * @param {string[]} permissions - The user permissions.
 * @returns {MenuItem[]} - The filtered menu items.
 */
export const filterMenuItems = (
  items: MenuItem[],
  permissions: string[]
): MenuItem[] => {
  return items
    .map((item) => {
      // Check if the item has permissions and if the user has at least one
      const hasPermission =
        !item.permission ||
        item.permission.some((p) => permissions.includes(p));

      // Filter children recursively
      const filteredChildren = item.children
        ? filterMenuItems(item.children, permissions)
        : undefined;

      // Include the item if it has permission or has filtered children
      if (hasPermission || (filteredChildren && filteredChildren.length > 0)) {
        return {
          ...item,
          children: filteredChildren,
        };
      }

      // Exclude the item if it has no permissions and no valid children
      return null;
    })
    .filter(Boolean) as MenuItem[]; // Remove null values
};

/**
 * Format a 10 digit phone number to (xx) xxxxxx xxxxxx
 *
 * @param {string} phoneNumber - The phone number to be formatted
 * @returns {string} - The formatted phone number
 */
export const formatPhoneNumber = (phoneNumber: string) =>
  `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2, 6)} ${phoneNumber.slice(
    6
  )}`;

/**
 * Format a 10 digit mobile phone number to xxxx xxx xxx
 *
 * @param {string} mobilePhone - The mobile phone number to be formatted
 * @returns {string} - The formatted mobile phone number
 */
export const formatMobilePhone = (mobilePhone: string) =>
  `${mobilePhone.slice(0, 4)} ${mobilePhone.slice(4, 7)} ${mobilePhone.slice(
    7
  )}`;

/**
 * Formats a subcontractor status code into a human-readable string
 * that includes any applicable progress codes.
 *
 * @param {string} status - The subcontractor status code
 * @param {SubcontractorStatusProgress[]} progressInfo - An array of
 *   objects containing the progress codes and their corresponding
 *   values. The value is used to determine the label for the progress
 *   code.
 * @returns {string} - The formatted subcontractor status string
 */
export const handleSubcontractorStatus = (
  status: string,
  progressInfo: SubcontractorStatusProgress[]
) => {
  const progress = progressInfo.map((item) => item.code);
  const values = [status];
  if (progress.length) {
    values.push(progress.join('→'));
  }

  return values.join(': ');
};

/**
 * Replaces placeholders in a string with corresponding values from an object.
 *
 * @param {string} str - The string with placeholders.
 * @param {SubstitutionObject} [params] - The object containing the replacement
 *   values. If not provided, the input string is returned unchanged.
 * @returns {string} - The string with placeholders replaced.
 */

export const replaceParams = (str: string, params?: SubstitutionObject) =>
  params !== undefined
    ? Object.keys(params).reduce(
        (current, key) => current.replace(`{${key}}`, `${params[key] ?? ''}`),
        str
      )
    : str;

/**
 * Extracts the filename from a Content-Disposition header string.
 *
 * @param {string} contentDisposition - The Content-Disposition header
 *   string.
 * @returns {string|null} - The filename if it could be extracted, or null
 *   if not.
 */
export const getFileName = (contentDisposition: string) => {
  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename=([^;]+)/);
    return fileNameMatch ? fileNameMatch[1] : null;
  } else {
    return 'filename-unknown';
  }
};

/**
 * Initiates a download of the specified file data.
 *
 * Creates an anchor element, sets its href to a URL representing the file data,
 * and triggers a click to prompt the user to download the file with the given
 * filename.
 *
 * @param {Blob} data - The file data to be downloaded as a Blob.
 * @param {string} fileName - The name that should be given to the downloaded file.
 */

export const downloadFile = (data: Blob, fileName: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(data);
  link.download = fileName;
  link.click();
};

/**
 * Takes an array of permissions and a list of objects with a 'key' and a
 * 'permission' property, and returns an object with the same keys as the list
 * of objects, where the value for each key is a boolean indicating whether the
 * permission is included in the list of permissions.
 *
 * @param {string[]} permissions - An array of permissions to check.
 * @param {{ key: string; permission: string }[]} permissionCheck - A list of
 *   objects with a 'key' and a 'permission' property.
 * @returns {Record<string, boolean>} - An object with the same keys as the
 *   list of objects, where the value for each key is a boolean indicating
 *   whether the permission is included in the list of permissions.
 */
export const checkPermissionObj = (
  permissions: string[],
  permissionCheck: { key: string; permission: string }[]
) => {
  const result: Record<string, boolean> = permissionCheck.reduce(
    (acc, { key, permission }) => {
      acc[key] = permissions.includes(permission);
      return acc;
    },
    {} as Record<string, boolean>
  );
  return result;
};

/**
 * Converts a given date string to the 'Australia/Melbourne' timezone
 * and formats it in a human-readable format, such as 'Today' or
 * 'Yesterday' if applicable, or in 'DD/MM/YYYY' format otherwise.
 *
 * @param {string} date - The date string to be converted and formatted.
 * @returns {string} - The formatted date string in 'Australia/Melbourne' timezone.
 */
export const formatLogDate = (date: string): string => {
  const timezone = 'Australia/Melbourne';
  const now = dayjs().tz(timezone);
  const inputDate = dayjs.utc(date).tz(timezone);

  if (inputDate.isSame(now, 'day')) {
    return 'Today';
  } else if (inputDate.isSame(now.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }

  return inputDate.format('DD/MM/YYYY');
};

/**
 * Takes an object and returns a new object with the same keys as the original
 * object, but with all values that are empty (i.e. undefined, null, empty string,
 * or empty array) removed.
 *
 * @param {any} obj - The object to be filtered.
 * @returns {any} - A new object with the same keys as the original object, but
 *   with all empty values removed.
 */
export const filterEmptyValues = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      return (
        value !== undefined &&
        value !== '' &&
        value !== null &&
        !(Array.isArray(value) && value.length === 0)
      );
    })
  );
};

/**
 * Takes an array of objects with 'id', 'displayName', and 'name' properties and
 * returns a new array of objects with 'value' and 'label' properties, suitable
 * for use as options in a Select component.
 *
 * @param {any[]} options - An array of objects with 'id', 'displayName', and
 *   'name' properties. If the array is empty, an empty array is returned.
 * @returns {Array<{value: string, label: string}>} - An array of objects with
 *   'value' and 'label' properties, suitable for use as options in a Select
 *   component.
 */
export const formatSelectOptions = (options: any[]) => {
  return options
    ? options.map(({ id, displayName, name }) => ({
        value: id,
        label: displayName ?? name,
      }))
    : [];
};

/**
 * Formats a given amount as an Australian currency string (AUD).
 *
 * @param {string | number} amount - The amount to be formatted. It can be a string
 *   representing a number or a number itself.
 * @returns {string} - The formatted currency string in AUD.
 */

export const formatCurrency = (amount: string | number) => {
  const parsedAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(parsedAmount);
};

/**
 * Given an array of objects with 'id', 'name', and 'status' properties, this
 * function returns an array of objects with 'value', 'label', and 'disabled'
 * properties, representing the inactive options. The 'disabled' property is
 * always true for the returned objects.
 *
 * @param {any[]} data - An array of objects with 'id', 'name', and 'status'
 *   properties.
 * @returns {{value: string, label: string, disabled: boolean}[]} - An array of
 *   objects with 'value', 'label', and 'disabled' properties, representing the
 *   inactive options.
 */
export const getInActive = (data: any[]) => {
  const inactiveOptions: { value: string; label: string; disabled: boolean }[] =
    [];
  if (data && data.length > 0) {
    data.forEach((item: any) => {
      if (item.status === BaseStatus.Inactive) {
        inactiveOptions.push({
          label: item.name,
          value: item.id,
          disabled: true,
        });
      }
    });
  }
  return inactiveOptions;
};

/**
 * Given a string, capitalize the first letter of the string.
 * If the string is empty or null, the original string is returned.
 *
 * @param {string} str - The string to capitalize the first letter of.
 * @returns {string} - The capitalized string.
 */
export const capitalize = (str: string): string => {
  if (!str) return str; // Return the original string if it's empty or null
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Formats a given float number as a string with the specified
 * minimum and maximum number of fraction digits.
 *
 * @param {object} options - The options object containing the value
 *   to be formatted, and the minimum and maximum number of fraction
 *   digits.
 * @param {number} options.value - The float number to be formatted.
 * @param {number} [options.minimumFractionDigits=2] - The minimum number
 *   of fraction digits to be displayed.
 * @param {number} [options.maximumFractionDigits=2] - The maximum number
 *   of fraction digits to be displayed.
 * @returns {string} - The formatted float number as a string.
 */
export const formatFloatNumber = ({
  value,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
}: {
  value: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}) => {
  return value.toLocaleString('en-AU', {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/**
 * Parses a given string representing a float number, which may have
 * thousand separators, and returns the parsed float number.
 *
 * @param {string} formattedValue - The string to be parsed, which may
 *   contain thousand separators (commas).
 * @returns {number} - The parsed float number.
 */
export const parseFormattedNumber = (formattedValue: string): number => {
  return parseFloat(formattedValue.replace(/,/g, ''));
};

/**
 * Reads a given File object and returns a Promise that resolves with the
 * contents of the file as a base64-encoded string.
 *
 * @param {File} file - The File object to read.
 * @returns {Promise<string>} - A Promise that resolves with the file contents
 *   as a base64-encoded string.
 */
export const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

/**
 * Returns a default value if the provided value is null or an empty string.
 *
 * @param {any} value - The value to check.
 * @returns {any} - The original value if it is not null or an empty string;
 *   otherwise, returns a predefined constant VALUE_EMPTY_TABLE.
 */

export const handleEmptyValue = (value: any) =>
  value === null || value === '' ? VALUE_EMPTY_TABLE : value;

/**
 * Determines the predefined date range label based on the start and end dates provided.
 * The function checks if the given date range matches any of the predefined ranges
 * such as 'Today', 'This Week', 'Last Week', etc. If the range does not match any
 * predefined range, it returns 'custom' or undefined based on the `undefinedForCustom` flag.
 *
 * @param {DatePickerType} startDateCheck - The start date to be checked against predefined ranges.
 * @param {DatePickerType} endDateCheck - The end date to be checked against predefined ranges.
 * @param {boolean} [undefinedForCustom] - Optional flag that determines if the function should return
 *   undefined instead of 'custom' when the date range does not match any predefined range.
 * @returns {string | undefined} - A string representing the matched predefined date range or 'custom'/undefined.
 */

export const determineDateRange = (
  startDateCheck: DatePickerType,
  endDateCheck: DatePickerType,
  undefinedForCustom?: boolean
): string | undefined => {
  const today = dayjs();

  if (!startDateCheck || !endDateCheck) {
    return 'custom'; // If either is null, it must be custom
  }

  // Ensure consistent granularity for comparisons
  const isToday =
    startDateCheck.isSame(today, 'day') && endDateCheck.isSame(today, 'day');

  const isThisWeek =
    startDateCheck.isSame(today.startOf('week'), 'day') &&
    endDateCheck.isSame(today.endOf('week'), 'day');

  const isLastWeek =
    startDateCheck.isSame(today.subtract(1, 'week').startOf('week'), 'day') &&
    endDateCheck.isSame(today.subtract(1, 'week').endOf('week'), 'day');

  const isThisMonth =
    startDateCheck.isSame(today.startOf('month'), 'day') &&
    endDateCheck.isSame(today.endOf('month'), 'day');

  const isLast7Days =
    startDateCheck.isSame(today.subtract(7, 'day').startOf('day'), 'day') &&
    endDateCheck.isSame(today.endOf('day'), 'day');

  const isLast14Days =
    startDateCheck.isSame(today.subtract(14, 'day').startOf('day'), 'day') &&
    endDateCheck.isSame(today.endOf('day'), 'day');

  const isLast30Days =
    startDateCheck.isSame(today.subtract(30, 'day').startOf('day'), 'day') &&
    endDateCheck.isSame(today.endOf('day'), 'day');

  const isLast45Days =
    startDateCheck.isSame(today.subtract(45, 'day').startOf('day'), 'day') &&
    endDateCheck.isSame(today.endOf('day'), 'day');

  const isLast60Days =
    startDateCheck.isSame(today.subtract(60, 'day').startOf('day'), 'day') &&
    endDateCheck.isSame(today.endOf('day'), 'day');

  if (isToday) return 'today';
  if (isThisWeek) return 'thisWeek';
  if (isLastWeek) return 'lastWeek';
  if (isThisMonth) return 'thisMonth';
  if (isLast7Days) return 'last7Day';
  if (isLast14Days) return 'last14Day';
  if (isLast30Days) return 'last30Day';
  if (isLast45Days) return 'last45Day';
  if (isLast60Days) return 'last60Day';

  return undefinedForCustom ? undefined : 'custom';
};

/**
 * Decodes a given JWT token and returns its decoded header, payload, and signature.
 *
 * @param {string} token - The JWT token to decode.
 * @returns {DecodedJWT} - An object containing the decoded header, payload, and signature.
 */
/**
 * Decoded JWT token object.
 *
 * @typedef {Object} DecodedJWT
 * @property {Record<string, unknown>} header - The decoded header.
 * @property {Record<string, unknown>} payload - The decoded payload.
 * @property {string} signature - The signature of the JWT token.
 */
export const decodeJWT = (token: string): DecodedJWT => {
  // Split the token into its three parts
  const [headerEncoded, payloadEncoded, signature] = token.split('.');

  // Decode the Base64Url encoded header and payload
  const decode = (str: string): Record<string, unknown> => {
    // Replace URL-safe Base64 characters and decode
    const decoded = atob(str.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  };

  return {
    header: decode(headerEncoded),
    payload: decode(payloadEncoded),
    signature,
  };
};

/**
 * Checks if a given JWT token is expired.
 *
 * @param {string | null} token - The JWT token to check.
 * @returns {boolean} - True if the token is expired, false if it is still valid.
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  const decoded = decodeJWT(token);
  const expirationTime = decoded.payload.exp;
  if (typeof expirationTime !== 'number') return true;

  return dayjs.unix(expirationTime).isBefore(dayjs());
};

/**
 * Check levenshteinDistance from 2 strings
 *
 * @param {string} str1 - First string compare
 * @param {string} str2 - Second string compare
 */
export const isEmptyValue = (
  value: string | null | undefined | number,
  values: [string | null, undefined] = [null, undefined]
) => {
  if (typeof value === 'number') return false;
  return values.includes(value);
};

/**
 * Check similarity percentage between two strings using partial_ratio
 *
 * @param {string | null | undefined} str1 - First string compare
 * @param {string | null | undefined} str2 - Second string compare
 */
export const similarityPercent = (
  str1: string | null | undefined,
  str2: string | null | undefined
) => {
  // If both strings are empty or null, they are 100% similar
  if (!str1 && !str2) return 100;

  // If either string is empty/null while the other is not, they are 0% similar
  if (!str1 || !str2) return 0;

  // Normalize string: lowercase, trim, remove spaces and special characters
  const normalize = (str: string) =>
    str
      .toLowerCase()
      .trim()
      .replace(/\s/, '')
      .replace(/[^a-z0-9]/gi, '');

  const words1 = normalize(str1);
  const words2 = normalize(str2);

  // Use partial_ratio (assumed to be imported from a string similarity library) to compute similarity
  return partial_ratio(words1, words2);
};

/**
 * Check similarity percentage between two strings using include for each
 *
 * @param {string | null | undefined} str1 - First string compare
 * @param {string | null | undefined} str2 - Second string compare
 */
const similaritySimplePercent = (
  str1: string | null | undefined,
  str2: string | null | undefined
) => {
  // If both strings are empty or null, they are 100% similar
  if (!str1 && !str2) return 100;

  // If either string is empty/null while the other is not, they are 0% similar
  if (!str1 || !str2) return 0;

  // Normalize string: lowercase, trim, remove spaces and special characters
  const normalize = (str: string) =>
    str
      .toLowerCase()
      .trim()
      .replace(/\s/, '')
      .replace(/[^a-z0-9]/gi, '');

  const words1 = normalize(str1);
  const words2 = normalize(str2);

  return words1.includes(words2) && words2.includes(words1) ? 100 : 0;
};

/**
 * Filter out and keep only the best-matching indexes above a similarity threshold (80%)
 *
 * @param {Record<string, number[]>} percent - A record where each key has an array of similarity scores
 * @returns {Record<string, number[]>} - A filtered record keeping only the strongest and unambiguous matches
 */
const subtractIndexPercent = (percent: Record<string, number[]>) => {
  // Minimum accepted similarity percentage
  const percentAccept = 80;
  const matched: Record<string, number[]> = {};

  // Recursive function to resolve conflicts and filter best matches
  const recursiveList = (destination: Record<string, number[]>) => {
    const matchedInside: Map<string, number> = new Map([]);
    const remain: Record<string, number[]> = {};

    // Iterate over each key to find unique best matches above the threshold
    _.map(destination, (value, key) => {
      const max = Math.max(...value);
      const isMatched =
        value.filter((v) => v === max && v >= percentAccept).length === 1;
      if (isMatched) {
        const indexMatched = value.findIndex((v) => v === max);

        // Ensure no duplicate index is matched
        if (!Array.from(matchedInside.values()).includes(indexMatched)) {
          matchedInside.set(key, indexMatched);
        } else {
          // Conflict detected: remove all entries using the same index
          for (const [key, value] of matchedInside.entries()) {
            if (value === indexMatched) {
              matchedInside.delete(key);
            }
          }
        }
      }
    });

    // Update matched and remain maps based on matchedInside
    _.map(destination, (value, key) => {
      if (Array.from(matchedInside.keys()).includes(key)) {
        matched[key] = value.map((v, i) =>
          matchedInside.get(key) === i ? v : 0
        );
      } else {
        remain[key] = value.map((v, i) =>
          Array.from(matchedInside.values()).includes(i) ? 0 : v
        );
      }
    });

    // Recursively continue to process unmatched items
    if (Object.keys(remain).length < Object.keys(destination).length) {
      recursiveList(remain);
    }
  };

  recursiveList(percent);

  return matched;
};

/**
 * Match headers from the source list to known destination system columns
 * by calculating similarity percentages and resolving best matches
 *
 * @param {Array<SYSTEM_COLUMN>} destination - List of system-defined column names
 * @param {string[]} source - List of column names to match (e.g. from a file)
 * @returns {Record<string, number[]>} - Matched similarity map of destination keys to index scores
 */
export const fitHeader = (
  destination: Array<SYSTEM_COLUMN>,
  source: string[]
) => {
  // Calculate similarity scores between each destination and all source headers
  const percents = destination.reduce((acc: Record<string, number[]>, cur) => {
    acc[cur] = source.map((h) => similaritySimplePercent(h, cur));
    return acc;
  }, {});

  // Use subtractIndexPercent to resolve best matches
  return subtractIndexPercent(percents);
};

/**
 * Capitalizes the display names in options
 * @param options - Array of options with display names
 * @returns New array with capitalized display names
 */
export const capitalizeOption = (
  options: { id: string; displayName: string }[]
): { id: string; displayName: string }[] =>
  options.map((option) => ({
    ...option,
    displayName: capitalize(option.displayName),
  }));

/**
 * get default router by permission
 *
 * @param {Array<string>} permissions - List of system-defined column names
 * @returns {string} - Matched similarity map of destination keys to index scores
 */
export const getRouteDefaultByPermission = (
  permissions?: string[]
): string | null => {
  if (!permissions) return null;

  const hasP1 = permissions.includes('display.viewDisPlay1');
  const hasP2 = permissions.includes('display.viewDisPlay2');

  // Case 1: ONLY Display 1 is present
  if (permissions.length === 1 && hasP1) return ROUTER_URL.DisplayOne;

  // Case 2: ONLY Display 2 is present
  if (permissions.length === 1 && hasP2) return ROUTER_URL.DisplayTwo;

  // Case 3: EXACTLY Display 1 and Display 2 are present
  if (permissions.length === 2 && hasP1 && hasP2) return ROUTER_URL.DisplayOne;

  // Fallback for everything else
  return ROUTER_URL.FactoryManagement;
};

export const minutesToHours = (
  minutes: number,
  decimals: number = 2
): number => {
  const factor = Math.pow(10, decimals);
  return Math.round((minutes / 60) * factor) / factor;
};
